import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Zoom from 'react-medium-image-zoom';
import { cn } from '@/lib/utils';
import 'react-medium-image-zoom/dist/styles.css';

interface MessageBubbleProps {
  children: React.ReactNode;
  isUser?: boolean;
  className?: string;
}

// 图片文件扩展名（更全面的列表）
const IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'svg',
  'bmp',
  'ico',
  'tiff',
  'tif',
  'avif',
  'apng',
  'heic',
  'heif'
];

// 图片相关的URL关键词
const IMAGE_KEYWORDS = [
  'chart',
  'image',
  'img',
  'photo',
  'picture',
  'pic',
  'graph',
  'diagram',
  'visualization',
  'plot',
  'render',
  'thumbnail',
  'preview'
];

/**
 * 通过URL模式快速判断是否为图片URL（同步，快速）
 */
const isImageUrlByPattern = (url: string): boolean => {
  if (!url) return false;

  const lowerUrl = url.toLowerCase().trim();

  // 1. 检查文件扩展名（支持查询参数）
  const urlWithoutQuery = lowerUrl.split('?')[0];
  const extension = urlWithoutQuery.split('.').pop();
  if (extension && IMAGE_EXTENSIONS.includes(extension)) {
    return true;
  }

  // 2. 检查URL路径中的关键词
  if (IMAGE_KEYWORDS.some(keyword => lowerUrl.includes(keyword))) {
    return true;
  }

  // 3. 检查常见的图片服务域名
  const imageDomains = [
    'imgur.com',
    'unsplash.com',
    'pexels.com',
    'pixabay.com',
    'quickchart.io',
    'chart.googleapis.com',
    'imagekit.io',
    'cloudinary.com',
    'imgix.net',
    'cdn.jsdelivr.net',
    'githubusercontent.com'
  ];
  if (imageDomains.some(domain => lowerUrl.includes(domain))) {
    return true;
  }

  return false;
};

/**
 * 通过尝试加载图片判断是否为图片URL（异步，最准确）
 */
const checkImageByLoading = (url: string): Promise<boolean> => {
  return new Promise(resolve => {
    const img = new Image();
    const timeout = setTimeout(() => {
      resolve(false);
    }, 5000); // 5秒超时

    img.onload = () => {
      clearTimeout(timeout);
      resolve(true);
    };

    img.onerror = () => {
      clearTimeout(timeout);
      resolve(false);
    };

    img.src = url;
  });
};

/**
 * 综合判断是否为图片URL
 * 优先使用快速模式匹配，对于不确定的URL可以异步验证
 */
const isImageUrl = (url: string): boolean => {
  return isImageUrlByPattern(url);
};

/**
 * 图片链接组件，支持加载失败时回退为普通链接
 */
const ImageLink: React.FC<{ href: string; linkText: string }> = ({ href, linkText }) => {
  const [isImage, setIsImage] = useState(true);
  const [hasError, setHasError] = useState(false);

  // 如果快速判断不确定，尝试异步验证
  useEffect(() => {
    if (!isImageUrlByPattern(href)) {
      // 对于不确定的URL，尝试异步验证
      checkImageByLoading(href)
        .then(result => {
          setIsImage(result);
        })
        .catch(() => {
          setIsImage(false);
        });
    }
  }, [href]);

  // 如果确认不是图片或加载失败，显示为普通链接
  if (!isImage || hasError) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 underline hover:text-blue-800"
      >
        {linkText || href}
      </a>
    );
  }

  // 显示为图片
  return (
    <div className="my-2">
      <Zoom>
        <img
          src={href}
          alt={linkText || '图片'}
          className="max-h-64 max-w-full cursor-zoom-in rounded-lg border border-gray-200 object-contain transition-opacity hover:opacity-80"
          onError={e => {
            setHasError(true);
            // 如果图片加载失败，显示错误信息
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const errorDiv = document.createElement('div');
            errorDiv.className =
              'flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500';
            errorDiv.textContent = '图片加载失败，请检查链接是否有效';
            target.parentElement?.appendChild(errorDiv);
          }}
        />
      </Zoom>
    </div>
  );
};

const MessageBubble: React.FC<MessageBubbleProps> = ({
  children,
  isUser = false,
  className = ''
}) => {
  // 将 children 转换为字符串
  const content = typeof children === 'string' ? children : String(children);

  // 自定义渲染组件
  const components = {
    // 处理链接
    a: ({ node, href, children: linkChildren, ...props }: any) => {
      // 如果是图片链接，直接渲染为图片（使用 react-medium-image-zoom 预览）
      if (href && isImageUrl(href)) {
        return <ImageLink href={href} linkText={String(linkChildren)} />;
      }
      // 普通链接
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-800"
          {...props}
        >
          {linkChildren}
        </a>
      );
    },
    // 处理表格
    table: ({ node, ...props }: any) => (
      <div className="my-4 overflow-x-auto">
        <table className="min-w-full border-collapse border border-gray-300 text-sm" {...props} />
      </div>
    ),
    thead: ({ node, ...props }: any) => <thead className="bg-gray-100" {...props} />,
    tbody: ({ node, ...props }: any) => <tbody {...props} />,
    tr: ({ node, ...props }: any) => <tr className="border-b border-gray-200" {...props} />,
    th: ({ node, ...props }: any) => (
      <th className="border border-gray-300 px-4 py-2 text-left font-semibold" {...props} />
    ),
    td: ({ node, ...props }: any) => <td className="border border-gray-300 px-4 py-2" {...props} />,
    // 处理段落，保留换行
    p: ({ node, ...props }: any) => <div className="mb-2 last:mb-0" {...props} />
  };

  if (isUser) {
    return (
      <div className={cn('flex justify-end text-left', className)}>
        <div className="relative rounded-lg bg-gray-100 px-2 py-1 text-left">
          <span className="whitespace-pre-wrap break-all text-left text-sm text-gray-900">
            {content}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex justify-start text-left', className)}>
      <div className="relative rounded-lg bg-blue-50 px-2 py-1 text-left">
        {/* AI消息的小三角 - 左上方 */}
        <div className="absolute -top-1 left-5 h-0 w-0 border-b-[6px] border-l-[6px] border-r-[6px] border-b-blue-50 border-l-transparent border-r-transparent"></div>
        <div className="prose prose-sm max-w-none whitespace-pre-wrap break-words text-sm text-gray-900">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
            {content}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
